<template>
    <div class="ai-tools-page">
        <!-- 页面标题 H1（仅对 SEO 和屏幕阅读器可见） -->
        <h1 class="sr-only">
            {{ t('tools.aiToolsTitle') }} - {{ t('tools.aiToolsSubtitle') }}
        </h1>

        <!-- SEO fallback: SSR-visible text links (no img/video), to keep indexing stable even on mobile SSR -->
        <ul class="sr-only" aria-label="AI 工具列表">
            <li v-for="item in [...imageTools, ...videoTools]" :key="item.path">
                <NuxtLink :to="item.path">
                    {{ item.label }} - {{ item.desc }}
                </NuxtLink>
            </li>
        </ul>

        <!-- Branch by User-Agent (SSR-safe): mobile users never render desktop cards => won't load desktop img/video -->
        <template v-if="isMobileDevice">
            <!-- Mobile: LabCard view (client-only, because CommonWaterfall uses window/ResizeObserver) -->
            <ClientOnly>
                <div class="lab-mobile-tools">
                    <tool-common-waterfall :key="tab" :items="mobileToolsList" :isWaterfall="false" :baseWidth="170"
                        rowKey="id" :fixedHeight="100" :gap="10">
                        <template #item="{ item, isWaterfall }">
                            <NuxtLink v-if="!item.isModal" :to="item.path"
                                class="block no-underline h-[100px] relative">
                                <laboratory-lab-card :item="item" :isWaterfall="isWaterfall" />
                            </NuxtLink>
                            <button v-else type="button"
                                class="block w-full no-underline h-[100px] relative text-left bg-transparent border-0 p-0"
                                @click="(e: MouseEvent) => handleToolClick(e, item)">
                                <laboratory-lab-card :item="item" :isWaterfall="isWaterfall" />
                            </button>
                        </template>
                    </tool-common-waterfall>
                </div>

                <!-- SSR + before-hydration fallback (no media requests) -->
                <template #fallback>
                    <div class="mobile-ssr-fallback">
                        <h2 class="mobile-ssr-title">
                            {{ t('tools.aiToolsTitle') }}
                        </h2>
                        <ul class="mobile-ssr-list">
                            <li v-for="item in mobileToolsList" :key="item.path" class="mobile-ssr-item">
                                <NuxtLink :to="item.path" class="mobile-ssr-link">
                                    {{ item.title }} - {{ item.desc }}
                                </NuxtLink>
                            </li>
                        </ul>
                    </div>
                </template>
            </ClientOnly>
        </template>

        <template v-else>
            <div ref="toolsContainerRef" class="tools-container">
                <div class="tool-category">
                    <div class="category-header">
                        <h2 class="category-title h-6 leading-6">
                            {{ t('tools.imageToolsCategory') }}
                        </h2>
                    </div>
                    <div class="tools-grid">
                        <template v-for="item in imageTools" :key="item.id">
                            <NuxtLink v-if="!item.isModal" :to="item.path" class="tool-card"
                                :style="{ width: `${columnWidth}px` }">
                                <div class="tool-card-inner">
                                    <div class="tool-image" :style="{
                                        width: `${columnWidth}px`,
                                        height: `${itemHeight}px`,
                                    }">
                                        <video v-if="item.video" class="media-content" :poster="item.bg"
                                            :src="item.video" loop autoplay muted playsinline webkit-playsinline
                                            preload="auto" :aria-label="`${item.label} 演示视频`" />
                                        <img v-else class="media-content" :src="item.bg"
                                            :alt="`${item.label} 工具预览`" loading="lazy" />
                                    </div>
                                    <div class="tool-info">
                                        <h3 class="tool-title">
                                            {{ item.label }}
                                        </h3>
                                        <p class="tool-desc">{{ item.desc }}</p>
                                    </div>
                                </div>
                            </NuxtLink>
                            <button v-else type="button" class="tool-card" :style="{ width: `${columnWidth}px` }"
                                @click="(e: MouseEvent) => handleToolClick(e, item)">
                                <div class="tool-card-inner">
                                    <div class="tool-image" :style="{
                                        width: `${columnWidth}px`,
                                        height: `${itemHeight}px`,
                                    }">
                                        <video v-if="item.video" class="media-content" :poster="item.bg"
                                            :src="item.video" loop autoplay muted playsinline webkit-playsinline
                                            preload="auto" :aria-label="`${item.label} 演示视频`" />
                                        <img v-else class="media-content" :src="item.bg"
                                            :alt="`${item.label} 工具预览`" loading="lazy" />
                                    </div>
                                    <div class="tool-info">
                                        <h3 class="tool-title">
                                            {{ item.label }}
                                        </h3>
                                        <p class="tool-desc">{{ item.desc }}</p>
                                    </div>
                                </div>
                            </button>
                        </template>
                    </div>
                </div>

                <div ref="videoCategoryRef" class="tool-category">
                    <div class="category-header">
                        <h2 class="category-title h-6 leading-6">
                            {{ t('tools.videoToolsCategory') }}
                        </h2>
                    </div>
                    <div class="tools-grid">
                        <template v-for="item in videoTools" :key="item.id">
                            <NuxtLink v-if="!item.isModal" :to="item.path" class="tool-card"
                                :style="{ width: `${columnWidth}px` }">
                                <div class="tool-card-inner">
                                    <div class="tool-image" :style="{
                                        width: `${columnWidth}px`,
                                        height: `${itemHeight}px`,
                                    }">
                                        <video v-if="item.video" class="media-content" :poster="item.bg"
                                            :src="item.video" loop autoplay muted playsinline webkit-playsinline
                                            preload="auto" :aria-label="`${item.label} 演示视频`" />
                                        <img v-else class="media-content" :src="item.bg"
                                            :alt="`${item.label} 工具预览`" loading="lazy" />
                                    </div>
                                    <div class="tool-info">
                                        <h3 class="tool-title">
                                            {{ item.label }}
                                        </h3>
                                        <p class="tool-desc">{{ item.desc }}</p>
                                    </div>
                                </div>
                            </NuxtLink>
                            <button v-else type="button" class="tool-card" :style="{ width: `${columnWidth}px` }"
                                @click="(e: MouseEvent) => handleToolClick(e, item)">
                                <div class="tool-card-inner">
                                    <div class="tool-image" :style="{
                                        width: `${columnWidth}px`,
                                        height: `${itemHeight}px`,
                                    }">
                                        <video v-if="item.video" class="media-content" :poster="item.bg"
                                            :src="item.video" loop autoplay muted playsinline webkit-playsinline
                                            preload="auto" :aria-label="`${item.label} 演示视频`" />
                                        <img v-else class="media-content" :src="item.bg"
                                            :alt="`${item.label} 工具预览`" loading="lazy" />
                                    </div>
                                    <div class="tool-info">
                                        <h3 class="tool-title">
                                            {{ item.label }}
                                        </h3>
                                        <p class="tool-desc">{{ item.desc }}</p>
                                    </div>
                                </div>
                            </button>
                        </template>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import {
    definePageMeta,
    useHead,
    useRequestURL,
    useRequestHeaders,
} from '#imports'
import { useI18n } from '~/composables/useI18n'
import { analyticsEvent } from '~/utils/log-event-func'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { useToolsHooks } from '~/utils/useHooks'
import { emitOpenPromptReversalModal } from '~/utils/emitter'
import { useAppStore } from '~/stores/app'

useMultilingualSeo({ pageType: 'aiTools' })

definePageMeta({
    layout: 'laboratory',
})

const appStore = useAppStore()
const { t, locale } = useI18n()
const route = useRoute()
const requestUrlHref = useRequestURL().href

const { aiImageToolsEnums, aiVideoToolsEnums } = useToolsHooks()

/**
 * 处理工具点击
 */
const handleToolClick = (e: MouseEvent, item: any) => {
    if (item.isModal) {
        e.preventDefault()
        emitOpenPromptReversalModal()
    }
}

// Normalize query.tab because vue-router query values can be string | string[] | null
const tab = computed<'image' | 'video'>(() => {
    const raw = route.query.tab
    const value = Array.isArray(raw) ? raw[0] : raw
    return value === 'video' ? 'video' : 'image'
})

// --- Device detection (SSR-safe) ---
const detectMobileByUserAgent = (userAgent?: string): boolean => {
    if (!userAgent) return false
    const ua = userAgent.toLowerCase()
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        ua
    )
}

// Treat bots as desktop to keep SEO stable / consistent.
const detectBot = (userAgent?: string): boolean => {
    if (!userAgent) return false
    const ua = userAgent.toLowerCase()
    return /googlebot|bingbot|slurp|duckduckbot|baiduspider|yandex|sogou|exabot|facebot|ia_archiver/i.test(
        ua
    )
}

const userAgent = (() => {
    if (import.meta.server) {
        const headers = useRequestHeaders()
        return String(headers['user-agent'] || '')
    }
    if (typeof navigator !== 'undefined') return navigator.userAgent
    return ''
})()

const isMobileDevice = computed(() => {
    const uaMobile = detectMobileByUserAgent(userAgent) && !detectBot(userAgent)
    // 客户端运行时，结合 appStore.isSmallScreen 实现响应式切换（DevTools 调试友好）
    if (import.meta.client) {
        return uaMobile || appStore.isSmallScreen
    }
    return uaMobile
})

const mobileToolsList = computed(() =>
    tab.value === 'video' ? aiVideoToolsEnums.value : aiImageToolsEnums.value
)

// 图片工具列表
const imageTools = computed(() =>
    aiImageToolsEnums.value.map((item: any) => ({
        id: item.id,
        label: item.title,
        path: item.path,
        desc: item.desc,
        isModal: item.isModal,
        bg: item?.[locale.value]?.thumbnail || item.thumbnail,
        video: item?.[locale.value]?.video || item.video,
    }))
)

// 视频工具列表
const videoTools = computed(() =>
    aiVideoToolsEnums.value.map((item: any) => ({
        id: item.id,
        label: item.title,
        path: item.path,
        desc: item.desc,
        isModal: item.isModal,
        bg: item?.[locale.value]?.thumbnail || item.thumbnail,
        video: item?.[locale.value]?.video || item.video,
    }))
)

// 获取站点基础URL
const siteUrl = computed(() => {
    // 在服务端和客户端都使用固定的生产域名
    // 这样可以确保结构化数据中的URL一致
    return 'https://www.weryai.com.cn'
})

// 构建完整的视频页面URL
const buildFullUrl = (path: string) => {
    const baseUrl = siteUrl.value
    // 确保路径以 / 开头
    const normalizedPath = path.startsWith('/') ? path : `/${path}`
    return `${baseUrl}${normalizedPath}`
}

const canonicalUrl = computed(() => buildFullUrl(route.path))

// 生成所有视频的 VideoObject 结构化数据
const videoStructuredData = computed(() => {
    const allTools = [...imageTools.value, ...videoTools.value]
    const videosWithData = allTools.filter(tool => tool.video)

    if (videosWithData.length === 0) return null

    const currentPageUrl = buildFullUrl(route.path)

    // 为每个视频创建 VideoObject
    const videoObjects = videosWithData.map(tool => {
        const videoPageUrl = buildFullUrl(tool.path)

        return {
            '@type': 'VideoObject',
            // name 是 Google 必填字段，如果未提供则使用路径生成默认名称
            name: tool.label || `AI Tool - ${tool.path.replace(/\//g, '')}`,
            // description 是 Google 必填字段，如果未提供则使用 name
            description:
                tool.desc ||
                tool.label ||
                `AI Tool - ${tool.path.replace(/\//g, '')}`,
            contentUrl: tool.video, // 视频文件的直接URL
            thumbnailUrl: tool.bg, // 视频缩略图
            // 视频所在页面的URL（指向工具详情页，这是视频的主要观看页面）
            // 这是关键字段，告诉Google视频在哪个页面上
            url: videoPageUrl,
            embedUrl: videoPageUrl, // 嵌入URL，通常与页面URL相同
            // 添加发布者信息
            publisher: {
                '@type': 'Organization',
                name: '微鲤AI',
                url: siteUrl.value,
            },
            // uploadDate 是 Google 必填字段，必须是 ISO 8601 格式且包含时区信息
            uploadDate: '2025-10-20T00:00:00+08:00',
        }
    })

    // 添加 ItemList 结构化数据，表明这是一个工具集合页面
    const itemList = {
        '@type': 'ItemList',
        '@id': `${currentPageUrl}#itemlist`,
        name:
            t('tools.imageToolsCategory') +
            ' & ' +
            t('tools.videoToolsCategory'),
        description: t('seo.aiTools.description') || 'AI Tools Collection',
        numberOfItems: allTools.length,
        itemListElement: allTools
            .filter(tool => tool.path) // 过滤掉没有落地页（即弹窗类）的工具，避免 SEO 死链
            .map((tool, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                item: {
                    '@type': 'SoftwareApplication',
                    // name 是必填字段
                    name:
                        tool.label ||
                        `AI Tool - ${tool.path.replace(/\//g, '')}`,
                    // description 是推荐字段，如果未提供则使用 name
                    description:
                        tool.desc ||
                        tool.label ||
                        `AI Tool - ${tool.path.replace(/\//g, '')}`,
                    url: buildFullUrl(tool.path),
                    applicationCategory: 'MultimediaApplication',
                    operatingSystem: 'Web',
                    offers: {
                        '@type': 'Offer',
                        price: '0',
                        priceCurrency: 'USD',
                        availability: 'https://schema.org/InStock',
                    },
                    ...(tool.video
                        ? {
                            video: {
                                '@type': 'VideoObject',
                                // name 是 Google 必填字段
                                name:
                                    tool.label ||
                                    `AI Tool - ${tool.path.replace(/\//g, '')}`,
                                contentUrl: tool.video,
                                thumbnailUrl: tool.bg,
                                // description 是 Google 必填字段
                                description:
                                    tool.desc ||
                                    tool.label ||
                                    `AI Tool - ${tool.path.replace(/\//g, '')}`,
                                // uploadDate 是 Google 必填字段
                                uploadDate: '2025-10-20T00:00:00+08:00',
                            },
                        }
                        : {}),
                },
            })),
    }

    // 使用 @graph 格式来包含多个结构化数据对象
    return {
        '@context': 'https://schema.org',
        '@graph': [...videoObjects, itemList],
    }
})

// 添加结构化数据到页面头部
useHead({
    link: computed(() => [{ rel: 'canonical', href: canonicalUrl.value }]),
    meta: computed(() => {
        // Keep query-tab URLs from being indexed as duplicates.
        return route.query.tab
            ? [{ name: 'robots', content: 'noindex,follow' }]
            : []
    }),
    script: computed(() => {
        // ⚠️ 必须为每个 JSON-LD script 提供稳定 key，避免 SSR/CSR 合并或响应式更新导致重复注入
        const scripts: Array<{ key: string; type: string; innerHTML: string }> =
            []

        // BreadcrumbList Schema
        scripts.push({
            key: 'breadcrumb-schema',
            type: 'application/ld+json',
            innerHTML: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                    {
                        '@type': 'ListItem',
                        position: 1,
                        name: '微鲤AI',
                        item: 'https://www.weryai.com.cn',
                    },
                    {
                        '@type': 'ListItem',
                        position: 2,
                        name: t('tools.aiToolsTitle'),
                        item: requestUrlHref,
                    },
                ],
            }),
        })

        // VideoObject + ItemList Schema
        if (videoStructuredData.value) {
            scripts.push({
                key: 'tools-graph-schema',
                type: 'application/ld+json',
                innerHTML: JSON.stringify(videoStructuredData.value),
            })
        }
        return scripts
    }),
})

// ===== 动态列宽配置（与 InspirationWaterFull 保持一致） =====
const config = {
    baseColumnWidth: 240,
    maxColumnWidth: 300,
    minColumnWidth: 180,
    gap: 16,
    mobileGap: 12,
    minColumns: 1,
}
const aspectRatio = 271 / 353 // 保持原有宽高比

const toolsContainerRef = ref<HTMLElement | null>(null)
const videoCategoryRef = ref<HTMLElement | null>(null)
const columnWidth = ref(config.baseColumnWidth)
const itemHeight = computed(() => Math.ceil(columnWidth.value / aspectRatio))

// 计算动态列宽
const calculateColumnWidth = () => {
    if (!toolsContainerRef.value) return

    const containerWidth = toolsContainerRef.value.offsetWidth
    const isMobile = window.innerWidth <= 768
    const currentGap = isMobile ? config.mobileGap : config.gap

    // 按基准列宽计算列数
    let columns
    if (isMobile) {
        columns = 2
    } else {
        columns = Math.floor(
            (containerWidth + currentGap) /
            (config.baseColumnWidth + currentGap)
        )
        columns = Math.max(columns, config.minColumns)
    }

    // 根据列数反推最佳列宽
    // 移动端需要减去容器内边距（16px * 2）
    const effectiveWidth = isMobile ? containerWidth - 32 : containerWidth
    let width = (effectiveWidth - (columns - 1) * currentGap) / columns

    // 非移动端限制列宽范围
    if (!isMobile) {
        width = Math.max(
            config.minColumnWidth,
            Math.min(config.maxColumnWidth, width)
        )
    }

    columnWidth.value = width
}

// 节流 + 防抖
let resizeTimer: ReturnType<typeof setTimeout> | null = null
let lastResizeTime = 0
const THROTTLE_DELAY = 150
const DEBOUNCE_DELAY = 300

const optimizedResize = () => {
    const now = Date.now()
    if (resizeTimer) clearTimeout(resizeTimer)

    if (now - lastResizeTime >= THROTTLE_DELAY) {
        lastResizeTime = now
        calculateColumnWidth()
    }

    resizeTimer = setTimeout(() => {
        lastResizeTime = Date.now()
        calculateColumnWidth()
    }, DEBOUNCE_DELAY)
}

let resizeObserver: ResizeObserver | null = null

// 页面浏览埋点
onMounted(() => {
    analyticsEvent('page_view_tools')

    // 初始化动态列宽
    if (toolsContainerRef.value) {
        calculateColumnWidth()
        resizeObserver = new ResizeObserver(optimizedResize)
        resizeObserver.observe(toolsContainerRef.value)
    }
})

// /ai-tools?tab=video: keep the same desktop UI, but scroll users to the video section.
watch(
    () => route.query.tab,
    tab => {
        if (!import.meta.client) return
        nextTick(() => {
            if (tab === 'video') {
                videoCategoryRef.value?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                })
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' })
            }
        })
    },
    { immediate: true }
)

onUnmounted(() => {
    if (resizeObserver) resizeObserver.disconnect()
    if (resizeTimer) clearTimeout(resizeTimer)
})
</script>

<style lang="less" scoped>
@import '@/assets/css/mixins.less';

// Screen reader only - 视觉隐藏但对 SEO 和屏幕阅读器可见
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.ai-tools-page {
    min-height: calc(100vh - 52px);
    width: 100%;
    padding: 0;
    padding-bottom: 30px;
}

.tools-container {
    max-width: none;
    margin: 0;
}

.lab-mobile-tools {
    width: 100%;
}

.mobile-ssr-fallback {
    padding: 16px;
}

.mobile-ssr-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 12px;
}

.mobile-ssr-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.mobile-ssr-link {
    display: block;
    text-decoration: none;
    color: #ffffff;
    opacity: 0.9;
}

.mobile-ssr-link:hover {
    opacity: 1;
}

.tool-category {
    margin-bottom: 40px;

    .category-header {
        display: flex;
        align-items: center;
        margin-bottom: 20px;

        .category-title {
            font-size: 24px;
            font-weight: 500;
            color: #ffffff;
        }
    }
}

.tools-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    justify-content: flex-start;
}

.tool-card {
    flex-shrink: 0;
    text-decoration: none;
    display: flex;
    flex-direction: column;
    cursor: pointer;
    background: transparent;
    border: 0;
    padding: 0;
    text-align: left;
    appearance: none;
    transition: transform 0.2s ease;

    &:hover {
        transform: translateY(-4px);
    }
}

.tool-card-inner {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.tool-image {
    position: relative;
    border-radius: 10px;
    overflow: hidden;
    background: #1b1d25;

    .media-content {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.3s ease;
    }
}

.tool-card:hover .tool-image .media-content {
    transform: scale(1.05);
}

.tool-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.tool-title {
    color: #ffffff;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 600;
    line-height: normal;
    margin: 0;
}

.tool-desc {
    color: #adadad;
    font-family: 'Roboto', sans-serif;
    font-size: 13px;
    font-weight: 400;
    line-height: normal;
    margin: 0;
}

// 响应式设计
@media (max-width: 768px) {
    .tools-container {
        padding: 0 16px 16px;
    }

    .tool-category {
        margin-bottom: 32px;

        .category-header {
            margin-bottom: 16px;

            .category-title {
                font-size: 20px;
            }
        }
    }

    .tools-grid {
        gap: 12px; // 移动端间距稍微缩小一点
        justify-content: flex-start;
    }

    .tool-info {
        gap: 2px;

        .tool-title {
            font-size: 14px;
        }

        .tool-desc {
            font-size: 12px;
            // 限制描述行数，避免在小屏上高度不一
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
            line-clamp: 2;
            overflow: hidden;
        }
    }
}
</style>
